import { Component, OnInit, OnDestroy, HostListener, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { QueensService, QueensCell } from '../services/queens.service';
import { LanguageService } from '../services/language.service';
import { SeoService } from '../services/seo.service';
import { AdSenseBlockComponent } from '../shared/adsense-block/adsense-block.component';

@Component({
  selector: 'app-queens',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, AdSenseBlockComponent],
  template: `
    <div class="game-container">
      <div class="game-ad-container">
        <app-adsense-block [adSlot]="'9526827533'"></app-adsense-block>
      </div>
      <header class="game-header">
        <button class="btn-back" [routerLink]="['/']">
          <span class="material-symbols-outlined">arrow_back</span>
        </button>
        <h1>{{ t('queens_puzzle') }}</h1>
        <div class="timer" [class.stopped]="gameWon">
          <span class="material-symbols-outlined">{{ gameWon ? 'check_circle' : 'timer' }}</span>
          {{ formatTime(seconds) }}
        </div>
      </header>

      <main class="game-content">
        <div class="controls glass-card">
          <!-- Settings Section (Hidden by default) -->
          <div class="settings-panel" [class.expanded]="showSettings">
            <div class="setting-group">
              <label>{{ t('difficulty') }}</label>
              <select class="premium-select" [(ngModel)]="difficulty" (change)="onDifficultyChange()">
                <option value="easy">{{ t('easy') }} (6x6)</option>
                <option value="medium">{{ t('medium') }} (8x8)</option>
                <option value="hard">{{ t('hard') }} (10x10)</option>
              </select>
            </div>

            <button class="btn-primary" (click)="startNewGame()">{{ t('restart') }}</button>

            <button class="btn-secondary" (click)="showRules = !showRules">
              <span class="material-symbols-outlined">help</span>
              {{ t('how_to_play') }}
            </button>

            <!-- Mobile Only Footer Links (Inside Menu) -->
            <div class="mobile-footer-links">
              <a [routerLink]="['/privacy-policy']">{{ t('privacy_title') }}</a>
              <span class="separator">•</span>
              <a [routerLink]="['/contact']">{{ t('contact_title') }}</a>
            </div>
          </div>
          
          <!-- Gameplay Actions (Always visible) -->
          <div class="game-actions" *ngIf="gameStarted">
            <button class="action-btn" (click)="getHint()">
              <span class="material-symbols-outlined">lightbulb</span>
              {{ t('hint') }}
            </button>
            <button class="action-btn" (click)="undo()" [disabled]="history.length === 0">
              <span class="material-symbols-outlined">undo</span>
              {{ t('undo') }}
            </button>
            <button class="action-btn" (click)="clearGrid()">
              <span class="material-symbols-outlined">delete_sweep</span>
              {{ t('clear') }}
            </button>
            <!-- Mobile Menu Toggle -->
            <button class="action-btn mobile-menu-btn" (click)="showSettings = !showSettings" [class.active]="showSettings">
              <span class="material-symbols-outlined">{{ showSettings ? 'close' : 'settings' }}</span>
              Menu
            </button>
          </div>

          <!-- Hint Box with Slide Animation -->
          <div class="hint-box" [class.visible]="!!hintText">
            <p>{{ hintText }}</p>
          </div>
        </div>

        <div class="board-wrapper">
          <!-- Touch Action None prevents scroll while dragging -->
          <div class="queens-board" 
               [style.--size]="size" 
               [style.grid-template-columns]="'repeat(' + size + ', 1fr)'"
               (touchmove)="handleTouchMove($event)"
               (touchstart)="handleTouchStart($event)"
               (touchend)="handleTouchEnd()">
            <div *ngFor="let row of cells; let rIdx = index; trackBy: trackByIndex" class="queens-row">
              <div *ngFor="let cell of row; let cIdx = index; trackBy: trackByIndex" 
                   class="queens-cell"
                   [attr.data-r]="rIdx"
                   [attr.data-c]="cIdx"
                   [style.background-color]="getRegionColor(cell.regionId)"
                   [class.marked]="marks[rIdx][cIdx] || isAutoMarked[rIdx][cIdx]"
                   [class.error]="isError(rIdx, cIdx)"
                   [class.hinted]="hintedCell?.r === rIdx && hintedCell?.c === cIdx"
                   (mousedown)="onMouseDown(rIdx, cIdx, $event)"
                   (mouseenter)="onMouseEnter(rIdx, cIdx)"
                   (contextmenu)="$event.preventDefault()">
                <span class="material-symbols-outlined icon-queen" *ngIf="cell.hasQueen">crown</span>
                <span class="material-symbols-outlined icon-mark" *ngIf="(marks[rIdx][cIdx] || isAutoMarked[rIdx][cIdx]) && !cell.hasQueen">close</span>
              </div>
            </div>
          </div>
          
        </div>
      </main>
      
      <div class="rules-overlay" *ngIf="showRules" (click)="showRules = false">
        <div class="rules-modal" (click)="$event.stopPropagation()">
          <div class="modal-header">
            <h3>{{ t('how_to_play') }}</h3>
            <button class="btn-close" (click)="showRules = false">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>
          
          <div class="rules-section">
            <h4>{{ t('queens_goal_title') }}</h4>
            <p>{{ t('queens_goal_text') }}</p>
          </div>

          <div class="rules-section">
            <h4>{{ t('queens_how_title') }}</h4>
            <ul>
              <li>{{ t('queens_how_1') }}</li>
              <li>{{ t('queens_how_2') }}</li>
              <li>{{ t('queens_how_3') }}</li>
            </ul>
          </div>

          <div class="rules-section">
            <h4>{{ t('queens_rules_title') }}</h4>
            <ul>
              <li>{{ t('queens_rule_row') }}</li>
              <li>{{ t('queens_rule_col') }}</li>
              <li>{{ t('queens_rule_zone') }}</li>
              <li>{{ t('queens_rule_touch') }}</li>
            </ul>
          </div>

          <div class="rules-note">
            <p><strong>Note:</strong> {{ t('tango_rules_note') }}</p>
          </div>
        </div>
      </div>

      <div class="win-modal" *ngIf="gameWon">
        <div class="modal-content glass-card">
          <span class="material-symbols-outlined icon-win">emoji_events</span>
          <h2>{{ t('victory') }}</h2>
          <p>{{ t('queens_win_msg') }} {{ t('time') }} : {{ formatTime(seconds) }}</p>
          <button class="btn-restart" (click)="startNewGame()">{{ t('restart') }}</button>
        </div>
      </div>

       <footer class="game-footer">
        <a [routerLink]="['/privacy-policy']">{{ t('privacy_title') }}</a>
        <span class="separator">•</span>
        <a [routerLink]="['/contact']">{{ t('contact_title') }}</a>
      </footer>
    </div>
  `,
})
export class QueensComponent implements OnInit, OnDestroy {
  size = 8;
  difficulty: 'easy' | 'medium' | 'hard' = 'medium';
  cells: QueensCell[][] = [];
  marks: boolean[][] = [];
  isAutoMarked: boolean[][] = [];
  solution: { row: number, col: number }[] = [];
  errors: { row: number, col: number }[] = [];
  hintedCell: { r: number, c: number } | null = null;
  hintText: string | null = null;

  seconds = 0;
  timerInterval: any;
  gameStarted = false;
  gameWon = false;
  _showRules = false;
  get showRules() { return this._showRules; }
  set showRules(v: boolean) {
    this._showRules = v;
    if (isPlatformBrowser(this.platformId)) {
      if (v) document.body.classList.add('modal-open');
      else document.body.classList.remove('modal-open');
    }
  }
  showSettings = false;
  isDragging = false;
  dragTargetMark = false;
  history: { r: number, c: number, prevQueen: boolean, prevMark: boolean }[] = [];



  constructor(
    private queensService: QueensService,
    private langService: LanguageService,
    private seoService: SeoService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  t(key: string, params: any = {}) {
    return this.langService.translate(key, params);
  }

  ngOnInit() {
    this.langService.currentLang$.subscribe(lang => {
      if (lang === 'fr') {
        this.seoService.setPageData({
          title: 'Entraînement Queens - Inspiré des Jeux LinkedIn (Star Battle)',
          description: 'Entraînez-vous au jeu Queens (Star Battle) comme sur LinkedIn. Le meilleur endroit pour s\'exercer aux puzzles de logique avec couronnes.',
          keywords: ['queens linkedin', 'entraînement queens', 'star battle', 'puzzle logique', 'linkedin games', 'jeu des reines'],
          type: 'game'
        });
      } else {
        this.seoService.setPageData({
          title: 'Queens Training - Inspired by LinkedIn Games (Star Battle)',
          description: 'Practice the popular Queens (Star Battle) logic puzzle training style. Train your skills with unlimited puzzles. Free online game.',
          keywords: ['queens puzzle', 'linkedin queens', 'star battle', 'queens training', 'logic game', 'crown puzzle'],
          type: 'game'
        });
      }
    });
    if (isPlatformBrowser(this.platformId)) {
      this.startNewGame();
    }
  }


  ngOnDestroy() {
    this.stopTimer();
  }

  getRegionColor(id: number): string {
    const colors = [
      '#FF5252', '#7C4DFF', '#00E676', '#FFD740', '#00B0FF',
      '#FF4081', '#64FFDA', '#F4FF81', '#BCAAA4', '#B0BEC5'
    ];
    return colors[id % colors.length];
  }

  onDifficultyChange() {
    this.startNewGame();
  }

  startNewGame() {
    // Map difficulty to size
    const sizeMap = { 'easy': 6, 'medium': 8, 'hard': 10 };
    this.size = sizeMap[this.difficulty];

    const { cells, solution, prefilledMarks } = this.queensService.generatePuzzle(this.size, this.difficulty);
    this.cells = cells;
    this.solution = solution;
    this.marks = Array.from({ length: this.size }, () => Array(this.size).fill(false));
    this.isAutoMarked = Array.from({ length: this.size }, () => Array(this.size).fill(false));

    // Apply pre-filled marks
    prefilledMarks.forEach(m => {
      this.marks[m.row][m.col] = true;
    });

    this.errors = [];
    this.hintedCell = null;
    this.history = [];
    this.gameWon = false;
    this.gameStarted = true;
    this.resetTimer();
    this.startTimer();
  }

  clearGrid() {
    if (!this.gameStarted) return;
    for (let r = 0; r < this.size; r++) {
      for (let c = 0; c < this.size; c++) {
        this.cells[r][c].hasQueen = false;
        this.marks[r][c] = false;
      }
    }
    this.history = [];
    this.applyAutoMarks();
    this.validate();
  }

  undo() {
    if (!this.gameStarted) return;
    const last = this.history.pop();
    if (last) {
      this.cells[last.r][last.c].hasQueen = last.prevQueen;
      this.marks[last.r][last.c] = last.prevMark;
      this.applyAutoMarks();
      this.validate();
    }
  }

  @HostListener('window:mouseup')
  onMouseUp() {
    if (this.isDragging) {
      this.isDragging = false;
      this.applyAutoMarks();
      this.validate();
    }
  }

  onMouseDown(r: number, c: number, event: MouseEvent) {
    if (!this.gameStarted) return;
    event.preventDefault();

    this.hintedCell = null;
    this.hintText = null;

    this.history.push({
      r,
      c,
      prevQueen: this.cells[r][c].hasQueen,
      prevMark: this.marks[r][c]
    });

    const cell = this.cells[r][c];
    if (cell.hasQueen) {
      // Queen -> Empty
      cell.hasQueen = false;
      this.marks[r][c] = false;
    } else if (this.marks[r][c]) {
      // X -> Queen
      this.marks[r][c] = false;
      cell.hasQueen = true;
    } else {
      // Empty -> X
      this.marks[r][c] = true;
      // Start drag to place more X
      this.isDragging = true;
      this.dragTargetMark = true;
    }

    this.applyAutoMarks();
    this.validate();
  }

  onMouseEnter(r: number, c: number) {
    if (!this.isDragging || !this.gameStarted) return;

    // Track state for undo if it changes
    if (this.marks[r][c] !== this.dragTargetMark || this.cells[r][c].hasQueen) {
      this.history.push({
        r,
        c,
        prevQueen: this.cells[r][c].hasQueen,
        prevMark: this.marks[r][c]
      });

      this.cells[r][c].hasQueen = false;
      this.marks[r][c] = this.dragTargetMark;
      // We don't validate/auto-mark during drag for performance, only on MouseUp
    }
  }

  cycleCellState(r: number, c: number) {
    // This is now effectively handled by onMouseDown for single clicks
    // but kept as a reference or if we need specific click behavior later.
  }

  toggleMark(r: number, c: number, event: MouseEvent) {
    // Handled by onMouseDown
  }

  validate() {
    const { valid, errors } = this.queensService.validatePlacement(this.cells, this.size);
    this.errors = errors;
    if (valid) {
      this.gameWon = true;
      this.gameStarted = false;
      this.stopTimer();
    }
  }

  applyAutoMarks() {
    this.isAutoMarked = Array.from({ length: this.size }, () => Array(this.size).fill(false));

    const queens: { r: number, c: number, regionId: number }[] = [];
    for (let r = 0; r < this.size; r++) {
      for (let c = 0; c < this.size; c++) {
        if (this.cells[r][c].hasQueen) queens.push({ r, c, regionId: this.cells[r][c].regionId });
      }
    }

    queens.forEach(q => {
      for (let i = 0; i < this.size; i++) {
        if (i !== q.c) this.isAutoMarked[q.r][i] = true;
        if (i !== q.r) this.isAutoMarked[i][q.c] = true;
      }
      for (let r = 0; r < this.size; r++) {
        for (let c = 0; c < this.size; c++) {
          if (this.cells[r][c].regionId === q.regionId && (r !== q.r || c !== q.c)) {
            this.isAutoMarked[r][c] = true;
          }
        }
      }
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          const nr = q.r + dr;
          const nc = q.c + dc;
          if (nr >= 0 && nr < this.size && nc >= 0 && nc < this.size && (dr !== 0 || dc !== 0)) {
            this.isAutoMarked[nr][nc] = true;
          }
        }
      }
    });

    for (let r = 0; r < this.size; r++) {
      for (let c = 0; c < this.size; c++) {
        if (this.cells[r][c].hasQueen) {
          this.marks[r][c] = false;
        }
      }
    }
  }

  isError(r: number, c: number): boolean {
    return this.errors.some(e => e.row === r && e.col === c);
  }

  getHint() {
    const hint = this.queensService.getHint(this.cells, this.solution, this.marks, this.isAutoMarked);
    if (hint) {
      this.hintedCell = { r: hint.row, c: hint.col };
      this.hintText = hint.explanation;

      // Type-safe handling of the hint action
      if (hint.type === 'queen') {
        this.cells[hint.row][hint.col].hasQueen = true;
        this.marks[hint.row][hint.col] = false;
      } else {
        this.marks[hint.row][hint.col] = true;
        this.cells[hint.row][hint.col].hasQueen = false;
      }

      this.applyAutoMarks();
      this.validate();
    }
  }

  formatTime(totalSeconds: number): string {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  startTimer() {
    this.stopTimer();
    if (isPlatformBrowser(this.platformId)) {
      this.timerInterval = setInterval(() => this.seconds++, 1000);
    }
  }

  stopTimer() { if (this.timerInterval) clearInterval(this.timerInterval); }
  resetTimer() { this.seconds = 0; }

  trackByIndex(index: number, obj: any): any {
    return index;
  }

  // --- Touch Support for Drag-to-Fill ---
  handleTouchStart(event: TouchEvent) {
    if (this.gameWon) return; // No interaction if won
    // Prevent default to stop scrolling/zooming immediately
    // event.preventDefault(); // Optional here, but touch-action: none is better

    // We can rely on handleTouchMove to pick up the first cell, 
    // or manually trigger onMouseDown logic for the start.
    // Let's defer to move or just use the first touch.
    const touch = event.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement;
    if (element && element.classList.contains('queens-cell')) {
      // Find the cell coordinates (this is tricky without data attrs, but we can do it via event delegation or careful DOM lookup.
      // Better way: use the event target if it's correct.)
      // Actually, standard mousedown handles the "start" of interaction.
      // But we need to set isDragging state if it wasn't.
    }
    // Simplest: The user likely taps or starts dragging. 'mousedown' handler on the cell handles the 'tap'.
    // For drag, we just need to know we are in a 'drag' state if we move.
    this.isDragging = true;
  }

  handleTouchMove(event: TouchEvent) {
    if (this.gameWon) return;
    // prevent scroll
    event.preventDefault();

    const touch = event.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement;

    // Check if we are over a cell
    if (element && element.classList.contains('queens-cell')) {
      // Use standard connection to Angular context? No easy way.
      // We must infer row/col from the loop context.
      // BUT we are outside the loop here.
      // Solution: Add data-r and data-c attributes to the cell div.
      const r = parseInt(element.getAttribute('data-r') || '-1');
      const c = parseInt(element.getAttribute('data-c') || '-1');

      if (r !== -1 && c !== -1) {
        this.onMouseEnter(r, c);
      }
    }
  }

  handleTouchEnd() {
    this.onMouseUp();
  }
}
